import React from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import { usePageMeta } from "@/lib/seo";

export default function Privacy() {
  usePageMeta({
    title: "Privacy Policy — MonetizeGPS",
    description: "How MonetizeGPS handles your data: we only analyze publicly available website information.",
    path: "/privacy",
  });
  return (
    <div className="min-h-screen grain" data-testid="privacy-page">
      <Header />
      <main className="max-w-3xl mx-auto px-6 md:px-10 py-16">
        <div className="mb-10">
          <div className="font-mono-plex text-[10px] uppercase tracking-[0.3em] text-[#555] mb-3">
            § Privacy Policy · Last updated Jul 2026
          </div>
          <h1 className="font-serif-display text-4xl md:text-6xl font-black tracking-tighter leading-[0.9]">
            Your privacy,<br />in plain English.
          </h1>
          <p className="mt-6 text-lg text-[#333] leading-relaxed">
            MonetizeGPS is a tool that analyzes public URLs. This page explains what we do and don&apos;t collect — no lawyer-speak.
          </p>
        </div>

        <div className="border-2 border-[#111] bg-white divide-y-2 divide-[#111]">
          <Section title="What we collect">
            <ul className="list-disc pl-5 space-y-2">
              <li><strong>URLs you analyze.</strong> We store the URL and the resulting Blueprint in our database so you can view it later, share it, or rescan.</li>
              <li><strong>Your email + name + profile picture</strong> if you sign in via Google OAuth. Nothing else from your Google account.</li>
              <li><strong>Your IP address</strong> is stored once (per person) so we can rate-limit anonymous free lookups to prevent abuse. Not linked to your identity.</li>
              <li><strong>Purchase records</strong> (which plan, when) if you subscribe. Card details go directly to Stripe — we never see or store them.</li>
              <li><strong>Feedback</strong> (👍/👎 on individual recommendations) so we can improve the product.</li>
            </ul>
          </Section>

          <Section title="What we do NOT collect">
            <ul className="list-disc pl-5 space-y-2">
              <li>❌ Your browsing history — the extension only reads the URL of the tab you&apos;re actively analyzing, only when you click the extension icon.</li>
              <li>❌ Content from sites you visit — we don&apos;t inject scripts into pages, don&apos;t read page HTML from your browser, don&apos;t track what you do on other sites.</li>
              <li>❌ Cookies from sites you analyze — we never touch your login sessions or personal data on third-party sites.</li>
              <li>❌ Analytics beacons — no Google Analytics, no third-party trackers, no session recording.</li>
            </ul>
          </Section>

          <Section title="How we analyze URLs">
            <p>
              When you analyze a URL, our backend fetches the public HTML from that URL (like any web browser or search engine bot would) and sends the extracted text to our AI analysis provider, which returns a structured monetization report. We do not train models on your analyses, and our provider does not retain your data for training.
            </p>
            <p className="mt-3">
              We respect <code className="bg-[#F4F4F0] px-1">robots.txt</code>, rate-limit our requests to any single domain, and refuse to analyze sites that prohibit it (Twitter/X, LinkedIn, Reddit, Facebook, and any login-walled page).
            </p>
          </Section>

          <Section title="Who we share data with">
            <p>
              We use these third-party service categories strictly for the functionality of the app. None of them get more data than they need to do their job:
            </p>
            <ul className="list-disc pl-5 space-y-2 mt-3">
              <li><strong>Stripe</strong> — payment processing. Sees only what&apos;s needed to process your payment. (Named because Stripe requires public disclosure.)</li>
              <li><strong>Our email delivery provider</strong> — sends the emails you request (e.g. &quot;email me this Blueprint&quot;). Sees your email address and the email content.</li>
              <li><strong>Our AI analysis provider</strong> — automated analysis. Sees only the text content of the URL you asked us to analyze. Does not retain, log, or train on your data.</li>
              <li><strong>Google OAuth</strong> — sign-in only. Sees only what Google shares during OAuth handshake (email, name, profile picture).</li>
              <li><strong>Our cloud hosting provider</strong> — hosts the application. Sees data that passes through the app; bound by a Data Processing Agreement.</li>
            </ul>
            <p className="mt-3">
              We can disclose specific vendor names on request — email <a href="mailto:support@monetizegps.com" className="underline hover:text-[#FF331F]">support@monetizegps.com</a> and we&apos;ll share them within 5 business days. We never sell your data. We never share it with advertisers or data brokers.
            </p>
          </Section>

          <Section title="How long we keep it">
            <ul className="list-disc pl-5 space-y-2">
              <li><strong>Blueprints:</strong> Kept as long as your account is active. Delete individual Blueprints anytime from &quot;My Blueprints.&quot;</li>
              <li><strong>Anonymous IP records:</strong> Kept indefinitely to prevent abuse. Contains only the IP, no personal identifiers.</li>
              <li><strong>Account data:</strong> Kept until you delete your account. Email us at support@monetizegps.com to request deletion.</li>
              <li><strong>Payment records:</strong> Kept 7 years for tax and legal reasons (US requirement).</li>
            </ul>
          </Section>

          <Section title="Your rights">
            <p>
              You can:
            </p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li>Access all your data — visible in &quot;My Blueprints.&quot;</li>
              <li>Delete individual Blueprints anytime.</li>
              <li>Delete your entire account and all associated data by emailing <a href="mailto:support@monetizegps.com" className="underline hover:text-[#FF331F]">support@monetizegps.com</a>.</li>
              <li>Export your data on request.</li>
              <li>Object to any processing you don&apos;t like — just email us.</li>
            </ul>
            <p className="mt-3">
              GDPR (EU) and CCPA (California) rights apply if you&apos;re a resident of those regions.
            </p>
          </Section>

          <Section title="Cookies">
            <p>
              We use exactly ONE cookie: <code className="bg-[#F4F4F0] px-1">session_token</code>, set when you sign in. It&apos;s HTTP-only (JavaScript can&apos;t read it), Secure (HTTPS only), and expires after 7 days. That&apos;s it.
            </p>
            <p className="mt-3">
              No advertising cookies. No tracking pixels. No third-party cookies.
            </p>
          </Section>

          <Section title="Chrome Extension permissions">
            <p>The MonetizeGPS Chrome extension requests these permissions and no others:</p>
            <ul className="list-disc pl-5 space-y-2 mt-3">
              <li><code className="bg-[#F4F4F0] px-1">activeTab</code> — temporary access to the URL of the tab you&apos;re on, only when you click the extension icon. Never read without your explicit click.</li>
              <li><code className="bg-[#F4F4F0] px-1">storage</code> — save your local preferences (whether you&apos;ve seen the first-run notice, recent scans list). Not synced to any server.</li>
              <li><code className="bg-[#F4F4F0] px-1">host_permissions: monetizegps.com</code> — the extension talks to our backend only. It does NOT have permission to access any other website.</li>
            </ul>
          </Section>

          <Section title="Changes to this policy">
            <p>
              If we make material changes to this policy, we&apos;ll notify you by email (if you have an account) and update the &quot;Last updated&quot; date at the top of this page. Immaterial changes (typos, clarifications) may happen without notice.
            </p>
          </Section>

          <Section title="Contact">
            <p>
              Privacy questions, data requests, or complaints:{" "}
              <a href="mailto:support@monetizegps.com" className="underline hover:text-[#FF331F]">
                support@monetizegps.com
              </a>
            </p>
          </Section>
        </div>

        <div className="mt-10 text-center">
          <Link to="/" className="font-mono-plex text-xs uppercase tracking-[0.25em] hover:text-[#FF331F]">
            ← Back to MonetizeGPS
          </Link>
        </div>
      </main>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <details open className="p-6 md:p-8 group">
      <summary className="cursor-pointer font-serif-display text-2xl font-bold list-none flex justify-between items-start gap-4">
        <span>{title}</span>
        <span className="font-mono-plex text-2xl leading-none group-open:rotate-45 transition-transform text-[#555]">+</span>
      </summary>
      <div className="mt-4 text-base text-[#333] leading-relaxed">
        {children}
      </div>
    </details>
  );
}
